import { useState } from 'react';
import {
  DevHubAccordion,
  DevHubAccordionSummary,
  DevHubAccordionDetails,
  DevHubButton,
  DevHubBox,
  DevHubDialog,
  DevHubDialogTitle,
  DevHubDialogContent,
  DevHubDialogActions,
  DevHubDynamicIcon,
  DevHubIconButton,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';

interface FAQItem {
  question: string;
  answer: React.ReactNode;
}

const faqItems: FAQItem[] = [
  {
    question:
      'What is the appropriate action if the application is retired but we want to retain the repository? Can I leave a repo tagged to a retired application in DevHub?',
    answer:
      'Typically, the repo should be archived if the application is retired. If you need to retain the repo, associate it with an existing active application so that a point of contact is available to address any identified vulnerabilities.',
  },
  {
    question:
      'What are the guidelines for tagging DevHub ID? Does every repo in the org require a DevHub ID?',
    answer:
      'Yes. The intent is to ensure every repo has a mapped contact who can be reached if any vulnerabilities are identified.',
  },
  {
    question:
      'My team has a repo that holds CI/CD task/script files for Concourse that are shared across our other applications. The repo itself doesn\'t serve as an application — it\'s just there for other applications to pull tasks from. Is it necessary to create a "dummy" application in order to tie it to an ITRC application ID?',
    answer:
      'There is no need to create a new application just to map the repo. However, CI/CD scripts can potentially contain secrets, so the repo still needs to be mapped to an application whose owner can resolve any findings.',
  },
  {
    question:
      'Are there no use cases where a repo does not need to be mapped to an application ID?',
    answer:
      'Yes. The intent is to ensure every repo has a mapped contact who can be reached if any vulnerabilities are identified.',
  },
  {
    question: 'What to do with research and test repos?',
    answer:
      'For now, they can be left as-is since the primary goal is to get repos mapped for security vulnerability assignment.\n\n' +
      'That said, we want to remind everyone of the overall intention: to better remediate security vulnerabilities. We would like to partner with other teams to define a repository lifecycle that allows for experimentation, but most research and test repos are in service of an overarching application, so we are not encouraging people to arbitrarily create new applications.\n\n' +
      'If a repo is truly for a new proof of concept, we understand the use case of a repo without an app — but it should have a timeline associated with it. After a set number of days, the POC is either moving forward (and would need a registered application) or not.',
  },
  {
    question: 'What is the criteria to have an app in DevHub?',
    answer:
      'An application is a collection of entities that cooperate to perform some function. All applications should be registered in DevHub.\n\n' +
      'There are many different tiers of applications, but as a Fortune 30 company, Comcast requires adherence to certain security and privacy policies.',
  },
  {
    question: 'What about repos not owned by me?',
    answer:
      'The intent is to map all repos to applications so that a point of contact can be identified for addressing any scanned vulnerabilities. If you do not own the repo, the appropriate owner should perform the mapping.',
  },
  {
    question: 'Can we map an App ID to a GitHub Org instead of a repo?',
    answer:
      "No. An application is made up of components, which are typically individual repositories. While it's uncommon, orgs normally contain more than one repository, and those repositories should each be in service of a specific function. Applications are the collection of entities that cooperate to perform some function — mapping at the org level would not provide the necessary granularity.",
  },
  {
    question: 'Repos do not belong to an individual app.',
    answer:
      'If a repo serves multiple applications, please use a comma-separated list of App IDs when mapping.',
  },
  {
    question:
      'Can users map multiple apps to one repo? Which app do Snyk findings get assigned to in that case?',
    answer:
      'Yes. While not ideal, we believe this applies to a small minority of cases — shared libraries, documentation repos, etc. can serve multiple applications. DevHub will return the one-to-many mapping via our API, UI, and other interfaces.',
  },
];

interface MappingExerciseFAQModalProps {
  open: boolean;
  onClose: () => void;
}

const MappingExerciseFAQModal = ({
  open,
  onClose,
}: MappingExerciseFAQModalProps) => {
  return (
    <DevHubDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DevHubDialogTitle>
        GitHub Repo to AppId Mapping — FAQ
        <DevHubIconButton
          onClick={onClose}
          style={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <DevHubDynamicIcon iconName="Clear" />
        </DevHubIconButton>
      </DevHubDialogTitle>
      <DevHubDialogContent>
        <DevHubBox style={{ marginBottom: 16 }}>
          <DevHubTypography variant="body2" style={{ opacity: 0.8 }}>
            Common questions and answers from the GitHub Repo to AppId Mapping
            exercise.
          </DevHubTypography>
        </DevHubBox>
        {faqItems.map((item, index) => (
          <DevHubAccordion key={index}>
            <DevHubAccordionSummary
              expandIcon={<DevHubDynamicIcon iconName="ExpandMore" />}
            >
              <DevHubTypography variant="subtitle2">
                {item.question}
              </DevHubTypography>
            </DevHubAccordionSummary>
            <DevHubAccordionDetails>
              <DevHubTypography variant="body2">
                {item.answer || 'Answer coming soon.'}
              </DevHubTypography>
            </DevHubAccordionDetails>
          </DevHubAccordion>
        ))}
      </DevHubDialogContent>
      <DevHubDialogActions>
        <DevHubButton
          onClick={onClose}
          variant="contained"
          buttonColor="black"
          size="medium"
        >
          Close
        </DevHubButton>
      </DevHubDialogActions>
    </DevHubDialog>
  );
};

/**
 * Button that opens the Mapping Exercise FAQ modal.
 */
export const MappingExerciseFAQButton = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <DevHubBox display="inline-flex">
        <DevHubButton
          variant="outlined"
          textColor="black"
          size="medium"
          onClick={() => setOpen(true)}
        >
          Mapping Exercise FAQ
        </DevHubButton>
      </DevHubBox>
      <MappingExerciseFAQModal open={open} onClose={() => setOpen(false)} />
    </>
  );
};
